import type { FC } from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Box,
  Grid,
  Rating,
  Typography,
  useMediaQuery,
} from "@braintrust/braintrust-ui-components";
import { useReviewModal } from "@js/apps/reviews/components/review-modal/use-review-modal";
import {
  closeWriteReviewModal,
  openWriteReviewModal,
  WriteReviewModalInstance,
} from "@js/apps/reviews/components/review-modal/write-review-modal-instance";
import {
  getShouldAutoOpenJobReviewsModal,
  setAutoOpenJobReviewsModal,
} from "@js/apps/reviews/slice";
import { showWriteReviewFormAfterOpen } from "@js/apps/reviews/slice";
import ProgressBarWithLabel from "@js/components/progress-bar-with-label";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import type { EmployerPublicProfile } from "@js/types/employer";
import type { FreelancerPublic } from "@js/types/freelancer";
import { pluralize } from "@js/utils";

import { ReviewList } from "../review-list";
import { WriteReview } from "../write-review";

import style from "./style.module.scss";

type ReviewModalProps = {
  profile: EmployerPublicProfile | FreelancerPublic;
  reviewSubject: EnumType<typeof ENUMS.AccountType>;
};
export const ReviewModal: FC<ReviewModalProps> = ({
  profile,
  reviewSubject,
}) => {
  const isMobile = useMediaQuery("sm");
  const {
    review_count: reviewCount,
    total_jobs: totalJobs,
    stars_breakdown: reviewRates,
    average_responsiveness: averageResponsiveness,
    average_work_quality: averageWorkQuality,
    average_rating: averageRating,
    can_user_review_job: canUserReview,
  } = profile;

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    showWriteReviewFormAfterOpenReviewsModal,
    setShowWriteReviewForm,
    showWriteReviewForm,
    onSuccess,
  } = useReviewModal({
    closeReviewModal: closeWriteReviewModal,
  });

  const shouldOpenModalImmediately = useAppSelector(
    getShouldAutoOpenJobReviewsModal,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (shouldOpenModalImmediately) {
      dispatch(setAutoOpenJobReviewsModal(false));
      openWriteReviewModal();
    }
  }, [dispatch, shouldOpenModalImmediately]);

  useEffect(() => {
    return () => {
      closeWriteReviewModal();
      dispatch(setAutoOpenJobReviewsModal(false));
    };
  }, [dispatch]);

  return (
    <>
      <WriteReviewModalInstance
        onOpen={() => {
          if (showWriteReviewFormAfterOpenReviewsModal) {
            setShowWriteReviewForm(true);
            dispatch(showWriteReviewFormAfterOpen(false));
          }
        }}
        onClose={() => {
          setShowWriteReviewForm(false);

          if (
            Array.from(searchParams).length > 0 &&
            !!searchParams.get("write_review")
          ) {
            setSearchParams({});
          }
        }}
      >
        {!!reviewCount ? (
          <>
            <div className={style.reviewModalHead}>
              <Typography
                variant="title"
                className={style.reviewModalTitle}
                component="h1"
                fontWeight={400}
              >
                Reviews
              </Typography>
              <Grid container spacing={{ xs: 3, sm: 4 }}>
                <Grid
                  size={{
                    xs: 4,
                    sm: 3,
                  }}
                  className="center"
                >
                  {averageResponsiveness && averageWorkQuality && (
                    <div className={style.reviewModalSummaryValue}>
                      {Number(averageRating)}
                    </div>
                  )}
                  {!!reviewCount && (
                    <Typography component="p" color="grey-4">
                      {reviewCount} review{pluralize(reviewCount)}
                    </Typography>
                  )}
                  {canUserReview && (
                    <Typography
                      component="p"
                      size="small"
                      color="grey-4"
                      className={`${style.writeReviewButton} color-gray-4`}
                      onClick={() => setShowWriteReviewForm((prev) => !prev)}
                    >
                      Write a review
                    </Typography>
                  )}
                </Grid>

                <Grid
                  size={{
                    xs: 8,
                    sm: 6,
                  }}
                >
                  {Object.entries(reviewRates)
                    .reverse()
                    .map(([key, value]) => (
                      <div className={style.reviewModalStarsBox} key={key}>
                        <Rating
                          value={parseFloat(key)}
                          iconStyle={{ width: "24px", height: "24px" }}
                          sx={{ pointerEvents: "none" }}
                        />

                        <Box sx={{ whiteSpace: "nowrap" }}>
                          <ProgressBarWithLabel
                            value={(Number(value) / (reviewCount * 2)) * 100}
                            sx={{
                              backgroundColor: "var(--grey-1)",
                              color: "var(--white)",
                            }}
                            color="inherit"
                          />
                          <Typography
                            component="span"
                            size="small"
                            color="grey-2"
                            className={style.reviewModalRate}
                          >
                            {Number(value)}
                          </Typography>
                        </Box>
                      </div>
                    ))}
                </Grid>

                {!isMobile && (
                  <Grid
                    size={{
                      xs: 3,
                    }}
                  >
                    {!!totalJobs && (
                      <Typography
                        component="h2"
                        variant="title"
                        fontWeight={400}
                        size="small"
                        className={style.reviewModalInfo}
                      >
                        <Typography
                          component="span"
                          size="small"
                          color="grey-4"
                          fontWeight={500}
                          sx={{
                            textTransform: "uppercase",
                            display: "block",
                            letterSpacing: "2px",
                          }}
                        >
                          Total Jobs
                        </Typography>
                        {totalJobs}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </div>
            <div className={style.reviewModalBody}>
              {showWriteReviewForm && (
                <WriteReview
                  reviewSubject={reviewSubject}
                  reviewSubjectId={profile.id}
                  onSuccess={onSuccess}
                  onClose={closeWriteReviewModal}
                />
              )}
              <ReviewList
                direction="column"
                reviewSubject={reviewSubject}
                reviewSubjectId={profile.id}
              />
            </div>
          </>
        ) : (
          <div className={style.reviewModalBody}>
            <Typography
              variant="title"
              className={style.reviewModalTitle}
              component="h1"
              fontWeight={400}
            >
              Reviews
            </Typography>
            <WriteReview
              reviewSubject={reviewSubject}
              reviewSubjectId={profile.id}
              onSuccess={onSuccess}
              onClose={closeWriteReviewModal}
            />
          </div>
        )}
      </WriteReviewModalInstance>
    </>
  );
};
